import { initOptions } from './questions.js';
export const defaultStartQuestion = () => ({
    question: 'GenericIntroStep',
    stepName: 'start',
    props: {
        ledeIn: null,
    },
    isLocked: true,
});
export const defaultProfileQuestion = (title = 'What is your name and email?') => ({
    question: 'GenericUserStep',
    stepName: 'profile',
    props: {
        title: title,
        emailLabel: 'Email',
        isRequired: true,
    },
    isLocked: true,
});
export const defaultProfessionalQuestion = (title = 'Tell us about your role and where you work', ledeIn = "We'll use this data to introduce you to your match.") => ({
    question: 'SpecificProfessionalStep',
    stepName: 'professional',
    props: {
        title,
        ledeIn,
        isRequired: true,
    },
    isLocked: true,
});
export const defaultLinkedinQuestion = (title = 'Share your link') => ({
    question: 'SpecificLinkedinStep',
    stepName: 'social',
    props: {
        title: title,
        ledeIn: 'Please share a URL that will be used to introduce you to your match.',
        isRequired: true,
        variableConfig: ['url', 'linkedin', 'twitter'],
    },
    isLocked: true,
});
export const defaultPhoneNumberQuestion = (title = 'Share your phone number', ledeIn = 'As a backup plan in case of any calendar issues, please provide your phone number so that you can be reached via phone call or Whatsapp. This number will appear in the calendar invite.') => ({
    question: 'SpecificPhoneStep',
    stepName: 'phone',
    isDisabled: true,
    isLocked: false,
    props: {
        title,
        ledeIn,
        variableName: 'phone',
        isRequired: false,
    },
});
export const defaultExperienceQuestion = (title = 'How many years of professional experience do you have in your role?', options = [initOptions('0-2'), initOptions('3-5'), initOptions('6-9'), initOptions('10+')]) => ({
    question: 'SingleSelectStep',
    stepName: 'experience',
    props: {
        title,
        ledeIn: null,
        options,
        variableName: 'experience',
        variableDefault: null,
    },
    lockedProps: ['options'],
    isOrdered: true,
    isMatchingDisabled: false,
});
export const defaultCompanySizeQuestion = (title = 'How many employees does your company have?', options = [
    initOptions('<5'),
    initOptions('5-25'),
    initOptions('25-50'),
    initOptions('50-150'),
    initOptions('150-500'),
    initOptions('500-1000'),
    initOptions('1000+'),
]) => ({
    question: 'SingleSelectStep',
    stepName: 'company_size',
    props: {
        title,
        variableName: 'company_size',
        options,
    },
    lockedProps: ['options'],
    isDisabled: true,
    isOrdered: true,
    isMatchingDisabled: false,
});
export const defaultCompanyStageQuestion = (title = 'What best describes the stage your company is at?', options = [
    initOptions('Solo / Non-Funded'),
    initOptions('Angel / Pre-Seed'),
    initOptions('Series Seed'),
    initOptions('Series A'),
    initOptions('Series B'),
    initOptions('Series C'),
    initOptions('Series D'),
    initOptions('Acquired / IPO'),
]) => ({
    question: 'SingleSelectStep',
    stepName: 'company_stage',
    props: {
        title,
        variableName: 'company_stage',
        options,
    },
    lockedProps: ['options'],
    isDisabled: true,
    isOrdered: true,
    isMatchingDisabled: false,
});
export const defaultAreasOfInterestQuestion = (areasOfInterest, title = "I'm interested in discussing...") => ({
    question: 'MultiSelectStep',
    stepName: 'asks',
    props: {
        title,
        ledeIn: null,
        variableName: 'asks',
        variableDefault: [],
        options: areasOfInterest,
        numColumns: 1,
        other: {
            isEnabled: false,
        },
        wildcard: initOptions("I'm open to discussing any of these topics"),
        randomizeOptions: false,
        minSelect: 1,
        maxSelect: 3,
    },
    isMatchingDisabled: false,
});
export const defaultTimezoneQuestion = (title = 'What timezone are you on?', ledeIn = 'If you are in between any of the ones listed, pick the closest one to where you are.') => ({
    question: 'SingleSelectStep',
    stepName: 'timezone',
    props: {
        title,
        ledeIn,
        options: [
            {
                id: 'America/Los_Angeles',
                isDisabled: false,
                text: 'America/Los_Angeles',
            },
            {
                id: 'America/New_York',
                isDisabled: false,
                text: 'America/New_York',
            },
            {
                id: 'Europe/London',
                isDisabled: false,
                text: 'Europe/London',
            },
        ],
        variableName: 'timezone',
        variableDefault: [],
        variableConfig: ['override::skip'],
    },
    lockedProps: ['title', 'ledeIn', 'options'],
});
export const defaultTimeSlotsQuestion = (minSelect = 3, product) => {
    if (product === 'group')
        minSelect = 1;
    return {
        question: 'SpecificTimeslotStep',
        stepName: 'timeslots',
        props: {
            title: 'Select the dates/times that work best for you',
            ledeIn: product === 'group'
                ? `For matching and scheduling purposes, select the options that best fit your availability.`
                : `For matching and scheduling purposes, select the options that best fit your availability. All calendar invites will be modifiable by both parties, so you can always reschedule with your partner if needed.`,
            variableName: 'core___timeslots_utc',
            variableDefault: [],
            minSelect,
        },
    };
};
export const defaultFallbackQuestion = (product) => {
    const optionYes = initOptions('Yes');
    const optionNo = initOptions('No');
    return {
        question: 'SingleSelectStep',
        stepName: 'fallback',
        isDisabled: product === 'group',
        props: {
            title: 'One last question',
            ledeIn: "Every now and again we have one person left without a match. It's rare, but if this happens, would you be open to getting matched twice?",
            options: [optionYes, optionNo],
            variableName: 'fallback',
            variableDefault: product === 'group' ? [optionNo.id] : [optionYes.id],
            variableConfig: ['override::skip'],
        },
        isLocked: true,
        lockedProps: ['title', 'ledeIn', 'options'],
    };
};
export const defaultModerationQuestion = (product) => {
    const optionYes = initOptions('Yes');
    const optionNo = initOptions('No');
    return {
        question: 'SingleSelectStep',
        stepName: 'moderation',
        isDisabled: product === 'one-to-one',
        props: {
            title: 'Would you be open to moderating a group session?',
            ledeIn: null,
            options: [optionYes, optionNo],
            variableName: 'moderation',
            variableDefault: product === 'one-to-one' ? [optionNo.id] : [],
        },
        isLocked: true,
        lockedProps: ['title', 'ledeIn', 'options'],
    };
};
export const defaultDoneQuestion = (product) => ({
    question: 'GenericDoneStep',
    stepName: 'done',
    props: {
        title: 'You’re all set! We received your signup 👍',
        ledeIn: `You’ll receive an email with your ${product === 'group' ? 'group' : 'match'} soon. Feel free to close this window`,
    },
    isLocked: true,
});
export const generateDefaultForm = (areasOfInterest, product) => [
    defaultStartQuestion(),
    defaultProfileQuestion(),
    defaultProfessionalQuestion(),
    defaultLinkedinQuestion(),
    defaultPhoneNumberQuestion(),
    // Standard Single/Multi-Select Questions
    defaultExperienceQuestion(),
    defaultCompanySizeQuestion(),
    defaultCompanyStageQuestion(),
    // Custom questions have to be inserted BETWEEN and match questions and asks
    // ...
    // Areas of Interest Question
    defaultAreasOfInterestQuestion(areasOfInterest),
    // Timezone & Timeslots
    ...(product === 'one-to-one' ? [defaultTimezoneQuestion()] : []),
    defaultTimeSlotsQuestion(undefined, product),
    // Fallback and Moderation Questions
    ...(product === 'one-to-one' ? [defaultFallbackQuestion(product)] : []),
    ...(product === 'group' ? [defaultModerationQuestion(product)] : []),
    defaultDoneQuestion(product),
];
